// src/mobile_pages/const/Daily.jsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getWorkReports } from "../../api/const";
import { softDeleteDoc } from "../../api/papers";
import { Search, Plus, ChevronLeft, ChevronRight, Cloud, Thermometer, Calendar, User, Trash2 } from "lucide-react";

// 날짜 포맷팅 헬퍼
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

// 날씨 아이콘 매핑
const getWeatherEmoji = (weather) => {
  if (!weather) return "☀️";
  const w = weather.toLowerCase();
  if (w.includes("맑") || w.includes("clear") || w.includes("sunny")) return "☀️";
  if (w.includes("구름") || w.includes("cloud") || w.includes("흐림")) return "☁️";
  if (w.includes("비") || w.includes("rain")) return "🌧️";
  if (w.includes("눈") || w.includes("snow")) return "❄️";
  if (w.includes("안개") || w.includes("fog")) return "🌫️";
  return "☀️";
};

export default function Daily() {
  const { id } = useParams(); // 프로젝트 ID
  const navigate = useNavigate();

  const project = id;

  // 리스트 데이터
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // 기본 상태
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null); // 삭제 중인 dns

  // 검색
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // 모바일은 10개씩

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {
        project,
        title: q, // 제목/작성자 등 통합 검색(API 지원 확인 필요, 일단 title로)
        page,
        page_size: pageSize,
      };

      const data = await getWorkReports(params);
      setRows(data.results ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error("리스트 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) fetchReports();
  }, [project, page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchReports();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // 상세 보기 이동
  const handleRowClick = (dns) => {
    navigate(`${dns}/view`);
  };

  const handleDelete = async (e, docDns) => {
    e.stopPropagation();
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      setDeleting(docDns);
      await softDeleteDoc(docDns, true);
      alert("삭제되었습니다.");
      fetchReports();
    } catch (err) {
      console.error("삭제 실패:", err);
      alert("삭제 실패");
    } finally {
      setDeleting(null);
    }
  };

  const items = useMemo(() => rows ?? [], [rows]);
  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-lg px-4 pt-6 pb-8">
        {/* 헤더: 제목 + 새 공사일보 버튼 */}
        <header className="mb-5">
          <div className="flex items-center justify-between">
            <h1 className="text-[28px] font-bold tracking-tight text-slate-900">
              공사일보
            </h1>
            <button
              onClick={() => navigate("new")}
              className="flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-[14px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              <Plus size={18} strokeWidth={2.5} />
              새 문서 등록
            </button>
          </div>
          <p className="mt-1 text-[15px] text-slate-500">
            총 {total}건의 공사일보
          </p>
        </header>

        {/* 검색 영역 */}
        <section className="mb-4 space-y-2">
          {/* 검색 입력창 */}
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <Search size={18} className="text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="검색어를 입력하세요"
              className="flex-1 min-w-0 bg-transparent text-[15px] text-slate-900 placeholder-slate-400 outline-none"
            />
          </div>

          {/* 필터 + 검색 버튼 */}
          <div className="flex items-center gap-2">
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[14px] text-slate-700 outline-none shadow-sm"
            >
              <option value="title">제목</option>
              <option value="created_by">작성자</option>
              <option value="confirmer">확인자</option>
            </select>
            <button
              onClick={handleSearch}
              className="rounded-xl bg-slate-900 px-5 py-2.5 text-[14px] font-semibold text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              검색
            </button>
          </div>
        </section>

        {/* 목록 */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-[15px] text-slate-400">불러오는 중...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-[15px] text-slate-400">공사일보가 없습니다.</p>
          </div>
        ) : (
          <section className="space-y-3">
            {items.map((row) => (
              <div
                key={row.dns}
                onClick={() => handleRowClick(row.dns)}
                className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm active:scale-[0.98] transition-transform cursor-pointer"
              >
                {/* 삭제 버튼 */}
                <button
                  onClick={(e) => handleDelete(e, row.dns)}
                  disabled={deleting === row.dns}
                  className="absolute top-3 right-3 p-2 rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 active:scale-[0.95] transition-all disabled:opacity-50"
                >
                  {deleting === row.dns ? (
                    <span className="text-xs">삭제중</span>
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>

                {/* 날짜 (메인 정보) */}
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={16} className="text-blue-500" />
                  <span className="text-[16px] font-bold text-slate-900">
                    {formatDate(row.meta?.date)}
                  </span>
                </div>

                {/* 날씨 정보 */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-lg">{getWeatherEmoji(row.meta?.weather)}</span>
                    <span className="text-[13px] text-slate-600">
                      {row.meta?.weather || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Thermometer size={14} className="text-orange-500" />
                    <span className="text-[13px] text-slate-600">
                      {row.meta?.temp_min ?? "-"}° / {row.meta?.temp_max ?? "-"}°
                    </span>
                  </div>
                </div>

                {/* 작성자 / 확인자 정보 */}
                <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <User size={14} className="text-slate-400" />
                    <span className="text-[13px] text-slate-500">
                      작성: {row.created_by?.name ?? "-"}
                    </span>
                  </div>
                  <span className="text-slate-300">·</span>
                  <span className="text-[13px] text-slate-500">
                    확인: {row.confirmer ?? "-"}
                  </span>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* 페이지네이션 */}
        {!loading && items.length > 0 && (
          <footer className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-600 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.95]"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="flex items-center gap-1 text-[14px]">
              <span className="font-semibold text-slate-900">{page}</span>
              <span className="text-slate-400">/</span>
              <span className="text-slate-500">{totalPages}</span>
            </div>

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-600 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.95]"
            >
              <ChevronRight size={20} />
            </button>
          </footer>
        )}
      </main>
    </div>
  );
}
